import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Field, FieldRenderProps, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Checkbox, TextArea, TextAreaProps } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import { useRecoilCallback, useRecoilValueLoadable } from 'recoil';
import { Button, FieldWrapper, SubmitButton } from 'components';
import { DisputeEventTypes, DisputeStatus } from 'enums';
import { useAccountParam, useDisputeParam, useNavigation, useRefreshDispute } from 'hooks';
import { filteredDisputes, useAccountId, useRefreshAllDisputeSummaries, useUserInfo } from 'state';
import { DisputeSummary } from 'types';
import styles from './DisputeDialog.module.scss';

type DialogState = {
    /**
     * Indicates whether the submission is processing
     */
    submitting: boolean
};

const DialogContext = createContext<DialogState>({ submitting: false });

/**
 * The props for the DisputeDialog.
 */
export type DialogProps = {
    /**
     * The initial values for the form data
     */
    initialValues?: any,
    /**
     * Indicates whether the dialog is visible
     */
    show?: boolean,
    /**
     * The function to call when the dialog is closed
     */
    onClose: () => void
};

/**
 * The input data for the submission.
 */
export type DisputeEventData = {
    disputeNumber: string,
    eventDate: string,
    eventTypeId: DisputeEventTypes,
    isPublic: boolean,
    note: string,
};

/**
 * The submission result.
 */
export type DisputeEventResult = {
    /**
     * The error message if the submission failed
     */
    error?: string
};

/**
 * The props for the DisputeDialog.
 */
export type DisputeDialogProps = {
    /**
     * The function called when the submit button is clicked.
     * @param e The input data for the submission
     * @returns The result of the submission
     */
    onSubmit: (e: DisputeEventData) => Promise<DisputeEventResult>,
    /**
     * Indicates whether the dispute details should be refreshed upon successful submission
     */
    refreshDispute?: boolean,
    /**
     * Indicates whether the dispute tracking should be refreshed upon successful submission
     */
    refreshTracking?: boolean,
    /**
     * Indicates whether the dispute tracking should be refreshed upon successful submission
     */
    refreshFiles?: boolean,
    /**
     * Indicates whether the Is Public checkbox should be displayed; defaults to true
     */
    showIsPublic?: boolean,
    /**
     * Indicates whether the user should be routed to the next dispute upon successful submission
     */
    routeToNext?: boolean,
    /**
     * Indicates whether the comments label should be displayed; defaults to true of additional
     * fields are passed as children
     */
    showNoteLabel?: boolean,
    /**
     * The label for the submit button
     */
    submitLabel?: string,
    /**
     * The label for the submit button while the submission is processing
     */
    submittingLabel?: string,
    /**
     * The theme color for the submit button
     */
    submitThemeColor?: 'primary' | 'warning',
    /**
     * The title for the dialog
     */
    title: string
    /**
     * Indicates whether the dialog is to route to an internal user.
     */
    disableIsPublic?: boolean
} & DialogProps;

/**
 * DisputeDialog component renders a dialog component that allows a user to enter
 * a public or private comment and submit the form input.
 * 
 * Additional fields can be added to the form by passing them as children.
 * 
 * @param props - Dialog properties
 * @returns Rendered component
 */
export const DisputeDialog = (props: PropsWithChildren<DisputeDialogProps>) => {
    const {
        children,
        initialValues,
        onClose,
        onSubmit,
        refreshDispute = true,
        refreshTracking = true,
        refreshFiles = true,
        routeToNext,
        show,
        showIsPublic = true,
        showNoteLabel = !!children,
        submitLabel = 'Save',
        submittingLabel = 'Saving',
        title,
        disableIsPublic = false,
        submitThemeColor = 'primary'
    } = props;
    const disputeId = useDisputeParam();
    const [error, setError] = useState<string>();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const refreshDisputeData = useRefreshDispute(disputeId, refreshDispute, refreshTracking, refreshFiles);
    const refreshDisputeSummaries = useRefreshAllDisputeSummaries();
    const accountFromUrl = useAccountParam();
    const accountFromState = useAccountId();
    const accountId = accountFromUrl ?? accountFromState ?? '';
    const disputesLoadable = useRecoilValueLoadable(filteredDisputes(accountId));
    const { navigateToDispute, navigateToDisputes } = useNavigation();
    const { disputeQueueId } = useUserInfo();
    const nextDispute = useMemo(() => {
        if (disputesLoadable.state !== 'hasValue') {
            return undefined;
        }

        const disputes = disputesLoadable.contents as DisputeSummary[];
        const currIndex = disputes.findIndex((dispute) => dispute.disputeNumber === disputeId);

        if (currIndex < 0) {
            return undefined;
        }

        const matchingDispute = disputes.find((dispute, index) => (
            index > currIndex &&
            dispute.disputeStatusId !== DisputeStatus.Closed &&
            dispute.disputeQueueId === disputeQueueId
        ));

        return matchingDispute?.disputeNumber;
    }, [disputeId, disputeQueueId, disputesLoadable.contents, disputesLoadable.state]);
    const getNextDispute = useRecoilCallback(({ snapshot }) => async () => {
        const disputes = await snapshot.getPromise(filteredDisputes(accountId));
        const currIndex = disputes.findIndex((dispute) => dispute.disputeNumber === disputeId);

        if (currIndex < 0) {
            return undefined;
        }

        const nextDispute = disputes.find((dispute, index) => (
            index > currIndex &&
            dispute.disputeStatusId !== DisputeStatus.Closed &&
            dispute.disputeQueueId === disputeQueueId
        ));

        return nextDispute?.disputeNumber;
    }, [accountId, disputeId, disputeQueueId]);

    const handleClose = () => {
        setError(undefined);
        onClose();
    };

    const submit = async (data: any) => {
        if (submitting || !disputeId) return;
        setSubmitting(true);
        setError(undefined);
        const result = await onSubmit({
            ...data,
            disputeNumber: disputeId,
            eventDate: new Date().toISOString(),
        });
        if (result.error) {
            setSubmitting(false);
            setError(result.error);
        } else {
            if (routeToNext) {
                let nextDisputeNumber = nextDispute;

                if (!nextDisputeNumber && disputesLoadable.state !== 'hasValue') {
                    try {
                        nextDisputeNumber = await getNextDispute();
                    } catch {
                        nextDisputeNumber = undefined;
                    }
                }

                setSubmitting(false);
                refreshDisputeSummaries();
                handleClose();
                nextDisputeNumber ? navigateToDispute(nextDisputeNumber) : navigateToDisputes();
                return;
            }

            setSubmitting(false);
            handleClose();
            refreshDisputeData();
        }
    };

    return show ? (
        <Dialog title={<span className={styles.title}>{title}</span>} minWidth={400} onClose={handleClose} closeIcon={!submitting}>
            <Form initialValues={initialValues} onSubmit={submit} render={({ onChange, valid, valueGetter }: FormRenderProps) => (
                <FormElement>
                    <div className={styles.scrollableContent}>
                        <DialogContext.Provider value={{ submitting }}>
                            {children}
                        </DialogContext.Provider>
                        <Field
                            name="note"
                            component={Comments}
                            disabled={submitting}
                            label={showNoteLabel ? 'Comments' : undefined}
                            validator={commentValidator}
                        />
                        {error && <Error>{error}</Error>}
                        <div className={styles.footer}>
                            <div className={styles.publicPrivateCheckbox}>
                                {showIsPublic &&
                                    <Checkbox
                                        className={styles.public}
                                        disabled={submitting || disableIsPublic }
                                        label="Note is public"
                                        value={valueGetter('isPublic')}
                                        onChange={(e) => onChange('isPublic', { value: e.value  })}
                                    />
                                }
                            </div>
                            <div className={styles.dialogButtonGroup}>
                                <Button className={styles.cancelButton} themeColor="secondary" disabled={submitting} onClick={onClose}>Cancel</Button>
                                <SubmitButton
                                    disabled={!valid}
                                    label={submitLabel}
                                    submittingLabel={submittingLabel}
                                    isSubmitting={submitting}
                                    themeColor={submitThemeColor}
                                />
                            </div>
                        </div>
                    </div>
                </FormElement>
            )}
            />
        </Dialog>
    ) : null;
};

/**
 * DisputeField component is used to render additional input fields as children of DisputeDialog.
 * Used in place of Kendo Field component to automatically disable the field while the dialog
 * submission is processing.
 * 
 * @returns Rendered component
 */
export const DisputeField = (props: any) => {
    const { submitting } = useContext(DialogContext);
    return <Field {...props} disabled={submitting} />;
};

const Comments = (props: FieldRenderProps) => (
    <FieldWrapper required {...props}>
        <TextArea
            id="note"
            aria-required="true"
            ariaLabelledBy="note_label"
            rows={4}
            {...props as TextAreaProps}
        />
    </FieldWrapper>
);

const commentValidator = (value: string) => value ? '' : 'Note is required';
